import React, {
	Fragment,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import MapStars from "../../Insertions/MapStars";
import DefaultThumbnail from "../../../assets/images/DefaultThumbnail.png";
import "./style.scss";

// constants
import { LevelConstants } from "../../../constants";
import { GlobalContext } from "../../../App";
import pushHistory from "../../../functions/pushHistory";
import { useHistory } from "react-router-dom";

import DefaultAvatar from "../../../assets/images/DefaultAvatar.webp";
import { Link } from "@mui/material";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import UsernameAvatarDisplay from "../../Insertions/UsernameAvatarDisplay";

type ImageStarContainerProps = {
	level: any;
	hoverEvents?: boolean;
	clickHandler?: (e: React.MouseEvent) => void;
	authorClick?: boolean;
	hoverCard?: boolean;
};

export const getActiveStats = (level) => {
	const lvlStats = LevelConstants.LevelStats;
	return {
		...(level?.hasPlayed && {
			[lvlStats.PLAYS.name]: {
				color: "#2bff00ff",
				prependText: "[Played] ",
			},
		}),
		...(level?.hasCommented && {
			[lvlStats.COMMENTERS.name]: {
				color: "#23a0ffff",
				prependText: "[Commented] ",
			},
		}),
		...(level?.hasFavourited && {
			[lvlStats.FAVOURITES.name]: {
				color: "#ff007bff",
				prependText: "[Favourited] ",
			},
		}),
		...(level?.hasRated && {
			[lvlStats.RATES.name]: {
				color: "#e5ff00ff",
				prependText: "[Rated] ",
			},
		}),
	};
};

/**
 * ImageStarContainer is a React component that is used to display a level's thumbnail, along with some of its stats and information.
 *
 * @param {object} level - The level's data.
 * @param {boolean} hoverEvents - Whether this component should respond to hover events or not. Defaults to false.
 * @param {function} clickHandler - The function to call when the level is clicked. Defaults to a function that does nothing.
 * @returns {ReactElement} - The JSX element to render.
 */
export const ImageStarContainer: React.FC<ImageStarContainerProps> = ({
	level,
	hoverEvents = false,
	clickHandler = (e) => {
		if (e.ctrlKey) return;
		e.preventDefault();
	},
	authorClick = true,
	hoverCard = false,
}) => {
	const {gameProperties} = useContext(GlobalContext);
	const history = useHistory();
	const thumb = level?.thumbnail || DefaultThumbnail;

	const [transitioned, setTransitioned] = useState<boolean>(false);
	const [hover, setHover] = useState<boolean>(false);
	const [useDefaultThumb, setUseDefaultThumb] = useState<boolean>(false);
	const [delayScroll, setDelayScroll] = useState<boolean>(false);
	const [statsToMap, setStatsToMap] = useState<any>([]);

	const authorsRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);

	const otherAuthors = level?.otherAuthors || [];

	useEffect(() => {
		const image = new Image();
		image.src = thumb;
		image.onload = () => {
			setUseDefaultThumb(false);
		};
		image.onerror = () => {
			setUseDefaultThumb(true);
		};
	}, [thumb]);

	useEffect(() => {
		const container = authorsRef.current;
		if (!container) return;

		const onWheel = (e: WheelEvent) => {
			const { scrollLeft, scrollWidth, clientWidth } = container;

			const canScrollLeft = scrollLeft > 0;
			const canScrollRight = scrollLeft + clientWidth < scrollWidth;

			if (delayScroll) e.preventDefault();

			// scroll the container with the mouse
			if (
				(e.deltaY > 25 && canScrollLeft) ||
				(e.deltaY < -25 && canScrollRight)
			) {
				e.preventDefault();
				container.scrollLeft -= e.deltaY;
				setDelayScroll(true);
				setTimeout(() => setDelayScroll(false), 800);
			}
		};

		container.addEventListener("wheel", onWheel, { passive: false });

		return () => {
			container.removeEventListener("wheel", onWheel);
		};
	}, [delayScroll]);

	const backgroundImage = `url(${useDefaultThumb ? DefaultThumbnail : thumb})`;
	const greenStars = level?.rating >= 4.9 && level?.rates?.length > 5;
	const game = gameProperties?.find(
		(prop) => prop?.internalID === parseInt(level?.game),
	);

	const disappearStyle = {
		opacity:
			(!hoverEvents || !hover) && titleRef?.current?.offsetHeight
				? "1"
				: "0",
		transition: "opacity 0.25s ease-in-out",
	};

	const appearStyle = {
		opacity: !hoverEvents || hover ? "1" : "0",
		transition: "all 0.25s ease-in-out",
		...(!hoverEvents && { display: "none" }),
	};

	const updateHoverState = (bool) => {
		setHover(bool);
		setTransitioned(bool);
	};

	const displayedAuthors = [level?.author, ...(otherAuthors || [])];
	const lvlStatsArr = useMemo(
		() => Object.values(LevelConstants.LevelStats),
		[],
	);
	const lvlStats = LevelConstants.LevelStats;

	useEffect(() => {
		const newState = [...lvlStatsArr].filter(
			(stat) => !stat.wide && stat.name !== "rating",
		);

		if (
			containerRef?.current &&
			containerRef?.current?.clientHeight > 220
		) {
			newState.splice(0, 0, {
				name: "difficulty",
				icon: "extension",
				tooltip: "Difficulty",
				wide: false,
				default: false,
			});
		}

		setStatsToMap(newState);
	}, [lvlStatsArr]);

	const mappedAuthors = (
		<div className="level-authors" ref={authorsRef}>
			{displayedAuthors.map((author, key) =>
				(key === 0 && !hover) && hoverEvents ? (
					<Fragment key={author?._id + "-" + key + "-authordisplay"}>
						<UsernameAvatarDisplay
							user={level?.author}
							useAnchor={false}
							authorClick={authorClick}
							suppressHover={!hoverCard}
							style={{
								...disappearStyle,
							}}
						/>
					</Fragment>
				) : (
					<Fragment key={author?._id + "-" + key + "-authordisplay"}>
						<OverlayTrigger
							placement={
								otherAuthors.length > 0 ? "top" : "right"
							}
							delay={{ show: 250, hide: 400 }}
							overlay={
								<Tooltip id={"level-tooltip-" + author?._id}>
									{author?.username}
								</Tooltip>
							}>
							<img
								className="icon-avatar"
								onClick={(e) => {
									e.preventDefault();
									if (!authorClick) return;
									e.stopPropagation();

									pushHistory(
										history,
										`/users/${author?._id}`,
										e.ctrlKey,
									);
								}}
								alt={author?.username}
								src={author?.avatar || DefaultAvatar}
								onError={(e: any) => {
									e.target.src = DefaultAvatar;
								}}
								style={{
									...appearStyle,
									boxShadow: "unset",
									opacity: !hoverEvents || hover ? "1" : "0",
									pointerEvents: authorClick ? "auto" : "none",
								}}
							/>
						</OverlayTrigger>
					</Fragment>
				),
			)}
		</div>
	);

	const activeStats = getActiveStats(level);

	const difficultyColors = {
		easy: "#00c700ff",
		medium: "#ff9100ff",
		hard: "#ff4400ff",
		extreme: "#e00101ff",
	};

	const tags = level?.tags;
	const lastEdited = lvlStats.LASTEDITED;

	const levelStatsContainer = (
		<div className="level-stats-container" style={appearStyle}>
			<div className="level-stats-container__left">
				{statsToMap.map((stat, index) => {
					const statValue = level[stat.name];
					const parsedValue =
						statValue instanceof Array
							? statValue?.length
							: !isNaN(parseFloat(statValue))
								? Math.round(statValue * 10) / 10
								: (statValue ?? 0);
					const activeStat = activeStats?.[stat?.name];
					const isDifficulty = stat?.name === "difficulty";

					return (
						<Fragment key={stat.name + "-" + index}>
							<OverlayTrigger
								placement="right"
								delay={{ show: 250, hide: 400 }}
								overlay={
									<Tooltip id={"level-tooltip-" + stat.name}>
										{activeStat?.prependText || ""}
										{stat.tooltip}
									</Tooltip>
								}>
								<div
									style={{
										color: isDifficulty
											? difficultyColors[
													statValue?.toLowerCase()
												]
											: activeStat?.color || "",
									}}>
									<span className="material-icons-round notranslate">
										{stat.icon}
									</span>
									{parsedValue}
								</div>
							</OverlayTrigger>
						</Fragment>
					);
				})}
			</div>
			<div className="level-stats-container__right">
				{[
					lvlStats.POSTDATE,
					...(!!level?.[lastEdited.name] &&
					// also ensure postdate and last edited are not the same
					new Date(level?.[lastEdited.name]).getTime() !==
						new Date(level?.[lvlStats.POSTDATE.name]).getTime()
						? [lastEdited]
						: []), // only include last edited if it exists
				].map((stat, index) => (
					<OverlayTrigger
						key={stat.name + "-tooltip-" + index}
						placement="left"
						delay={{ show: 250, hide: 400 }}
						overlay={
							<Tooltip id={"level-tooltip-" + stat.name}>
								{stat.tooltip}
							</Tooltip>
						}>
						<div
							key={stat.name + "-defaultdisplay-" + index}
							style={{
								color: LevelConstants.StatusColors[
									level?.status.toUpperCase()
								],
							}}>
							<span>
								{moment(level?.[stat.name]).format(
									"MMM D, YYYY",
								)}
							</span>
							<span className="material-icons-round notranslate">
								{stat.icon}
							</span>
						</div>
					</OverlayTrigger>
				))}
				{tags?.length > 0 && (
					<div className="level-tags-container">
						{level?.tags.map((tag, index) => (
							<span
								className="level-tag"
								key={tag + "-level-tags-" + index}>
								{tag}
							</span>
						))}
					</div>
				)}
			</div>
		</div>
	);

	return (
		<div
			ref={containerRef}
			style={{
				backgroundImage: backgroundImage,
				...(transitioned ? {} : { zIndex: "5" }),
			}}
			onTouchStart={() => updateHoverState(true)}
			onTouchEnd={() => updateHoverState(false)}
			onTouchCancel={() => updateHoverState(false)}
			onMouseEnter={() => updateHoverState(true)}
			onMouseLeave={() => updateHoverState(false)}
			onTransitionEndCapture={() => setTransitioned(true)}
			className={
				level?.status?.toLowerCase() +
				" unselectable image-star-container" +
				(hoverEvents
					? ` __hover-events ${hover ? " __hover" : ""}`
					: " __no-hover-events")
			}>
			<Link
				onClick={clickHandler}
				href={`/levels/${level?._id}`}
				style={{ textDecoration: "none" }}>
				<div>
					{/* THESE STATS ARE VISIBLE BY DEFAULT */}
					<h5
						className="center-textoutput"
						ref={titleRef}
						style={{
							...disappearStyle,
							color: LevelConstants.StatusColors[
								level?.status?.toUpperCase()
							],
						}}>
						{level?.name}
					</h5>
					{game !== undefined && (
						<img
							className="game-icon"
							alt={game?.acronym}
							src={game?.cleanLogo}
							style={{
								...disappearStyle,
								bottom: `-12px`,
								right: "-12px",
							}}
						/>
					)}
					<MapStars
						level={level}
						rate={null}
						starCount={5}
						top={titleRef?.current?.offsetHeight || 0}
						color={greenStars ? "#2cd40f" : ""}
						width={window.innerWidth < 1000 ? "5mm" : "7mm"}
						height={window.innerWidth < 1000 ? "5mm" : "7mm"}
						style={{
							...disappearStyle,
							position: "absolute",
						}}
					/>

					{/* THESE STATS ONLY SHOW UP UPON HOVER */}
					{!!level?.author?._id && <>{mappedAuthors}</>}
					{levelStatsContainer}
				</div>
			</Link>
		</div>
	);
};

/**
 * A compact version of the LevelDisplay component, designed for use in level lists.
 *
 * @param {Level} level The level to display.
 * @param {function} [clickHandler] The function to call when the level is clicked.
 * @returns {JSX.Element} The compact level display element.
 */
export const CompactLevelDisplay = ({ level, clickHandler = (e) => {} }) => {
	const {gameProperties} = useContext<any>(GlobalContext);
	const game = gameProperties?.find((game) => game?.internalID === level?.game);
	const activeStats = getActiveStats(level);

	return (
		<>
			<OverlayTrigger
				placement="top"
				overlay={
					<Tooltip id="level-tooltip">
						<div className="author-tooltip">
							Tags: {level?.tags?.join(", ") || <i>None</i>}
						</div>
					</Tooltip>
				}>
				<Link
					onClick={clickHandler}
					href={`/levels/${level?._id}`}
					className={"compact-flex"}
					style={{
						textDecoration: "none",
						color: LevelConstants.StatusColors[
							level?.status?.toUpperCase()
						],
					}}>
					<span
						className={level?.status?.toLowerCase()}
						title={`[${level?.status}] ` + level?.name}>
						<span style={{ color: game?.colour }}>
							[{game?.acronym}]
						</span>
						{level?.name}
					</span>
					<span>
						{Object.values(LevelConstants.LevelStats)
							?.filter((stat) => stat?.default)
							?.map((stat) => {
								let value;
								const parsedStat = level[stat.name];
								const activeStat = activeStats?.[stat?.name];
								const checkmark = activeStat ? (
									<span className="wildheart-green">
										&#x2713;
									</span>
								) : (
									<></>
								);

								// arrays
								if (parsedStat instanceof Array) {
									value = parsedStat?.length || 0;
									// strings
								} else if (
									["author", "postDate"].includes(stat.name)
								) {
									value = (stat.name === "postDate"
										? moment(parsedStat).format(
												`MMM D, 'YY`,
											)
										: parsedStat?.username) || (
										//string
										<i className="gray">N/A</i>
									);
									// numbers
								} else {
									value =
										Math.round(parsedStat * 10) / 10 ||
										(stat?.name === "rating" ? (
											// NA specifically for no rating
											<i className="gray">N/A</i>
										) : (
											0 // number
										));
								}
								return (
									<span
										key={stat.name + "-stat"}
										title={
											stat.name === "postDate"
												? moment(parsedStat).format(
														`MMMM DD, YYYY`,
													)
												: value
										}
										style={{
											...(parsedStat?.username && {
												fontSize: "small",
											}),
										}}
										className={`${stat.wide ? "wide d-lg-inline d-none" : ""}`}>
										{value}
										{checkmark}
									</span>
								);
							})}
					</span>
				</Link>
			</OverlayTrigger>
			<hr />
		</>
	);
};
